import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { formatWallClockInstant, parseWallClockInstant } from './wallClockInstant';

/**
 * Shared booking date-time helpers — aligned with room booking flows.
 *
 * - Write: UTC `LocalDateTime` without `Z` (`RoomBookingStepperDialog`).
 * - Read (booking start/end): space-local wall clock (`MyBookingsPage`, `RoomBookingDetailsPage`).
 * - Read (calendar busy slots): UTC without `Z` (`TimeAndDateStep`).
 * - Read (change proposals): UTC-like without `Z` (`RoomBookingDetailsPage`).
 */

/** Serialize a picker `Date` for booking create/update APIs. */
export function serializeBookingInstantForApi(date: Date): string {
  return date.toISOString().replace('Z', '');
}

/** Interpret a picker Date's visible fields as wall-clock time in the supplied timezone. */
export function bookingWallClockToUtc(date: Date, timezone: string | null | undefined): Date {
  const normalizedTimezone = timezone?.trim();
  if (!normalizedTimezone) return date;
  return zonedTimeToUtc(formatWallClockInstant(date, 'seconds'), normalizedTimezone);
}

/** Represent a UTC instant as a Date whose local fields match the supplied timezone. */
export function bookingUtcToWallClock(date: Date, timezone: string | null | undefined): Date {
  const normalizedTimezone = timezone?.trim();
  if (!normalizedTimezone) return date;
  return utcToZonedTime(date, normalizedTimezone);
}

/** Serialize a picker wall-clock selection as the UTC LocalDateTime expected by booking APIs. */
export function serializeBookingWallClockForApi(
  date: Date,
  timezone: string | null | undefined,
): string {
  return serializeBookingInstantForApi(bookingWallClockToUtc(date, timezone));
}

/** Parse calendar event instants (UTC in DB, no `Z` suffix). */
export function parseBookingCalendarInstant(iso: string): Date {
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
}

/** Parse booking start/end returned from user/host booking APIs (space-local wall clock). */
export function parseBookingDisplayInstant(iso: string): Date {
  return new Date(iso);
}

/** Parse change-proposal instants (UTC-like values without `Z`). */
export function parseBookingProposalInstant(iso: string): Date {
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
}

/**
 * Format a booking start + duration as a date and time-slot label
 * (e.g. "Sat, Sep 5, 2026, 07:30–08:30").
 *
 * When `timezone` is set, `startIso` is treated as a UTC booking instant
 * (`yyyy-MM-ddTHH:mm[:ss][.sss][Z]`) and formatted in that zone.
 * Otherwise it is treated as a naive wall-clock string.
 */
export function formatBookingPaymentSlot(
  startIso: string,
  durationMinutes: number,
  locale: string,
  timezone?: string | null,
): string | null {
  const trimmed = startIso.trim();
  if (!trimmed || !Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return null;
  }
  const tz = timezone?.trim() || '';
  const start = tz ? parseBookingCalendarInstant(trimmed) : parseWallClockInstant(trimmed);
  if (Number.isNaN(start.getTime())) {
    return null;
  }
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const zoneOpts: Intl.DateTimeFormatOptions = tz ? { timeZone: tz } : {};
  const dateLabel = start.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...zoneOpts,
  });
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...zoneOpts,
  };
  return `${dateLabel}, ${start.toLocaleTimeString(locale, timeOpts)}–${end.toLocaleTimeString(locale, timeOpts)}`;
}

/**
 * Format booking `createdAt` as a local date-time display string.
 * Values are wall-clock local date-times from the API (same convention as start/end).
 * When `timezone` is provided, append a short zone name for clarity.
 */
export function formatBookingCreatedAt(
  createdAt: string,
  locale: string,
  timezone?: string | null,
): string {
  const date = parseBookingDisplayInstant(createdAt);
  const dateTimeStr = date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const normalizedTimezone = timezone?.trim();
  if (!normalizedTimezone) {
    return dateTimeStr;
  }
  try {
    const zoneLabel = new Intl.DateTimeFormat(locale, {
      timeZone: normalizedTimezone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;
    return zoneLabel ? `${dateTimeStr} (${zoneLabel})` : dateTimeStr;
  } catch {
    return dateTimeStr;
  }
}
